import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { Icon16Done } from '@vkontakte/icons';
import { Spacing } from '@vkontakte/vkui';
import promo2ImgDark from 'img/safe/safePromo2Dark.png';
import promo2Img from 'img/safe/safePromo2Light.png';
import promoImgDark from 'img/safe/safePromoDark.png';
import promoImg from 'img/safe/safePromoLight.png';
import React, { type ReactElement } from 'react';
import { helpSafeHref } from 'reactApp/appHelpers/featuresHelpers/features/overquotaSafe';
import { renderCommonWhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog.helpers';
import { sendDwhModal } from 'reactApp/modules/dwh/dwh.helpers';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import styles from './SafePromoDialog.css';

const nameBase = 'safe-promo-dlg';

export enum SafePromoType {
    base = 'base',
    beforeMove = 'beforeMove',
    moreAboutSafe = 'moreAboutSafe',
    manage = 'manage',
}

interface Args {
    promoType: SafePromoType;
    safeSize?: number;

    onClickRestore?(): void;

    onClickClearSafe?(): void;
}

const features: Record<SafePromoType, any[] | ReactElement> = {
    [SafePromoType.base]: [
        <>Сейф не расходует место в Облаке — можно&nbsp;сохранить всё,&nbsp;что&nbsp;лежит в корзине</>,
        <>Верните файлы, когда будет нужно — сразу все. Им&nbsp;понадобится свободное место</>,
        <>В сейфе поместится 1 ТБ файлов на срок до 3 месяцев</>,
    ],
    [SafePromoType.beforeMove]: [
        <>Сейф&nbsp;— пространство для&nbsp;файлов, которое не&nbsp;занимает в&nbsp;Облаке ни&nbsp;килобайта</>,
        <>Если в файлах было что‑то важное — можно вернуть сразу все. Они снова займут место в Облаке</>,
        <>В сейфе поместится 1 ТБ файлов на срок до 3 месяцев</>,
    ],
    [SafePromoType.moreAboutSafe]: [
        <>В сейф перемещаются файлы из корзины — до 1 ТБ.</>,
        <>Положите лишнее в сейф, если Облако переполнено — это может помочь временно снять ограничения</>,
        <>Файлы хранятся в сейфе 3 месяца, затем вернутся в&nbsp;Облако, сразу все. Но можно вернуть и раньше</>,
    ],
    [SafePromoType.manage]: <>Их можно вернуть — если в сейф попали нужные фото, видео или документы</>,
};

export const renderSafePromoDialog = (
    { promoType = SafePromoType.base, safeSize = 0, onClickClearSafe, onClickRestore }: Args = {
        promoType: SafePromoType.base,
        safeSize: 0,
    }
) => {
    const isDarkTheme = isDarkThemeModeEnabled();

    const name = `${nameBase}_${promoType === SafePromoType.base ? '' : promoType.toString()}`;

    const getTitle = () => {
        if (promoType === SafePromoType.beforeMove && safeSize) {
            return (
                <p className={styles.title}>
                    {bytesToNDigits(safeSize, 3).value} файлов из корзины теперь
                    <br />в сейфе — место в Облаке освободилось
                </p>
            );
        }

        if (promoType === SafePromoType.moreAboutSafe) {
            return <p className={styles.title}>Сейф&nbsp;— пространство для&nbsp;файлов, которое не&nbsp;расходует место в&nbsp;Облаке</p>;
        }

        if (promoType === SafePromoType.manage) {
            return <p className={styles.title}>Файлы из сейфа не занимают место в&nbsp;Облаке</p>;
        }

        return 'Ваши файлы будут в сейфе';
    };

    const getImage = () => {
        if (promoType === SafePromoType.manage) {
            return isDarkTheme ? promo2ImgDark : promo2Img;
        }

        return isDarkTheme ? promoImgDark : promoImg;
    };

    const featuresData = features[promoType];

    return renderCommonWhatsNewDialog({
        title: getTitle(),
        content: (
            <>
                <Spacing size={12} />
                <div className={styles.items}>
                    {Array.isArray(featuresData) ? (
                        featuresData.map((text, idx) => (
                            <div key={idx} className={styles.item}>
                                <div className={styles.icon}>
                                    <Icon16Done fill="var(--vkui--color_background_accent)" width={16} height={16} />
                                </div>
                                <span className={styles.text}>{text}</span>
                            </div>
                        ))
                    ) : (
                        <div className={styles.item}>
                            <span className={styles.text}>{featuresData}</span>
                        </div>
                    )}
                </div>
                <Spacing size={24} />
            </>
        ),
        primaryButtonText: promoType === SafePromoType.manage ? 'Вернуть файлы в Облако' : 'Понятно',
        secondaryButtonText: promoType === SafePromoType.manage ? 'Очистить сейф' : 'Узнать больше о сейфе',
        imageUrl: getImage(),
        onClick: () => {
            sendDwhModal({ action: 'click', name, button_name: 'ok' });

            if (promoType === SafePromoType.manage) {
                onClickRestore?.();
            }
        },
        onDismiss: () => {
            sendDwhModal({ action: 'close', name });
        },
        onSecondary: () => {
            sendDwhModal({ action: 'click', name, button_name: 'more' });

            if (promoType === SafePromoType.manage) {
                onClickClearSafe?.();
            } else {
                window.open(helpSafeHref, '_blank', 'noopener noreferrer');
            }
        },
        onShow: () => {
            sendDwhModal({ name });
        },
        dialogSize: 'tiny',
        buttonTheme: 'vk',
        className: styles.root,
        contentClassName: styles.content,
        imageClassName: styles.image,
        closeAfterClick: true,
    });
};
